import React, { useState } from "react";
import moment from "moment";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import { Icon, BranchDropDown, FormInput } from "../../../components/Component";
import DateRangePicker from "../../../components/input/date-time/DateRangePicker";
import FormDatePicker from "../../../components/input/date-time/FormDatePicker";
import YearsDropDown from "../../../components/dropdowns/YearsDropDown";
import MonthDropDown from "../../../components/dropdowns/MonthDropDown";
import { MONTHS } from "../../../utils/Utils";
import { bottomStartDatePickerPopperModifiers } from "../../../components/input/date-time/datePickerPopperModifiers";

registerLocale(enGB);

const FILTER_MONTHLY = "monthly";
const FILTER_WEEKLY = "weekly";
const FILTER_CUSTOM = "custom";

const filterOptions = [
  { label: "Monthly", value: FILTER_MONTHLY },
  { label: "Weekly", value: FILTER_WEEKLY },
  { label: "Custom", value: FILTER_CUSTOM },
];

const monthRange = (date) => ({
  startDate: moment(date).startOf("month").format("YYYY-MM-DD"),
  endDate: moment(date).endOf("month").format("YYYY-MM-DD"),
});

const CustomDateRangePicker = ({ startDate, endDate, onChange }) => {
  const [year, setYear] = useState();
  const [month, setMonth] = useState();

  return (
    <div className="form-control-wrap form-date-picker">
      <div className="input-group">
        <div className="input-group-prepend w-100 position-relative">
          <Icon className="input-group-text" name="calender-date" />
          <DatePicker
            locale={enGB}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={onChange}
            placeholderText="Select date range"
            dateFormat="dd/MM/yyyy"
            formatWeekDay={(dayName) => dayName.slice(0, 2)}
            className="form-control rounded-0 rounded-end border-start-0"
            popperPlacement="bottom-start"
            popperModifiers={bottomStartDatePickerPopperModifiers}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div className="d-flex justify-content-center">
                <div className="input-group">
                  <span
                    className="input-group-text px-1"
                    onClick={() => {
                      decreaseMonth();
                      setMonth();
                      setYear();
                    }}
                    disabled={prevMonthButtonDisabled}
                  >
                    <Icon name="chevron-left" />
                  </span>
                  <YearsDropDown
                    value={
                      year ?? {
                        id: "",
                        value: moment(date).format("YYYY"),
                        label: moment(date).format("YYYY"),
                      }
                    }
                    placeholder="years"
                    className="w-100px"
                    onSelect={(selectedYear) => {
                      setYear(selectedYear);
                      changeYear(selectedYear?.value);
                    }}
                  />
                  <MonthDropDown
                    value={month}
                    placeholder="months"
                    className="w-120px"
                    selectedValue={MONTHS.find((item) => item.value === moment(date).format("MMMM"))}
                    onSelect={(selectedMonth) => {
                      setMonth(selectedMonth);
                      changeMonth(selectedMonth?.id - 1);
                    }}
                  />
                  <span
                    className="input-group-text px-1"
                    onClick={() => {
                      increaseMonth();
                      setMonth();
                      setYear();
                    }}
                    disabled={nextMonthButtonDisabled}
                  >
                    <Icon name="chevron-right" />
                  </span>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

const TimesheetReportFilter = ({ onRangeChange, branch, onBranchChange, searchText, onSearchChange }) => {
  const [filterType, setFilterType] = useState(FILTER_WEEKLY);
  const [monthDate, setMonthDate] = useState(new Date());
  const [customStart, setCustomStart] = useState(new Date());
  const [customEnd, setCustomEnd] = useState(new Date());

  const handleFilterType = (type) => {
    setFilterType(type);
    if (type === FILTER_MONTHLY) {
      const range = monthRange(monthDate);
      onRangeChange(range.startDate, range.endDate);
    }
    if (type === FILTER_CUSTOM && customStart && customEnd) {
      onRangeChange(moment(customStart).format("YYYY-MM-DD"), moment(customEnd).format("YYYY-MM-DD"));
    }
  };

  const handleMonthChange = (date) => {
    if (!date) return;
    setMonthDate(date);
    const range = monthRange(date);
    onRangeChange(range.startDate, range.endDate);
  };

  const handleCustomChange = (dates) => {
    const [start, end] = dates;
    setCustomStart(start);
    setCustomEnd(end);
    if (!start || !end) return;
    onRangeChange(moment(start).format("YYYY-MM-DD"), moment(end).format("YYYY-MM-DD"));
  };

  return (
    <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: "16px" }}>
      <div style={{ width: "240px", flex: "0 0 240px" }}>
        <FormInput
          isShowLabel={false}
          value={searchText ?? ""}
          placeholder="Request ID, Client/Carer, Ward"
          onTextChange={onSearchChange}
        />
      </div>
      <div style={{ width: "225px", flex: "0 0 225px" }}>
        <BranchDropDown
          value={branch}
          isClearable
          isShowAll
          placeholder="Area Manager"
          onSelect={onBranchChange}
        />
      </div>
      <div className="d-flex align-items-center flex-nowrap" style={{ gap: "14px" }}>
        {filterOptions.map((item) => (
          <label key={item.value} className="radio-label d-flex align-items-center mb-0">
            <input
              className="me-1 mt-0"
              type="radio"
              name="timesheetReportFilter"
              checked={filterType === item.value}
              onChange={() => handleFilterType(item.value)}
            />
            <span className="radio-label-text">{item.label}</span>
          </label>
        ))}
      </div>
      {filterType === FILTER_MONTHLY && (
        <div style={{ width: "190px", flex: "0 0 190px" }}>
          <FormDatePicker selected={monthDate} onChange={handleMonthChange} showMonthYearPicker />
        </div>
      )}
      {filterType === FILTER_WEEKLY && (
        <div className="timesheet-report-week-filter">
          <DateRangePicker onDateChange={onRangeChange} />
        </div>
      )}
      {filterType === FILTER_CUSTOM && (
        <div style={{ width: "300px", flex: "0 0 300px" }}>
          <CustomDateRangePicker startDate={customStart} endDate={customEnd} onChange={handleCustomChange} />
        </div>
      )}
    </div>
  );
};

export default TimesheetReportFilter;
